import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type {
  ActivityDimensions,
  ActivityDimensionValues,
  ActivityModelsResponse,
  ActivityObservedDimensions,
  ActivityReviewPriority,
} from '@okvc/schema';

import { ApiClient } from './api.client.js';
import { LogsApiKeys } from './logs.api.js';
import { PatientsApiKeys } from './patients.api.js';

// ── Types (mirror @okvc/schema; kept local so the web-app doesn't pull a
// server-side lib through Vite's bundle).

export type ActivityEventTrigger = 'scheduled' | 'motion';

export type ActivityReviewVerdict = 'accepted' | 'rejected';

export interface UpdateActivityConfigBody {
  enabled: boolean;
  /** Required when `enabled` is true. 3–60 min. Omit to leave the cadence as-is. */
  intervalMinutes?: number | null;
  /** Required when `enabled` is true. 8–30 s. Omit to leave as-is. */
  clipDurationSeconds?: number | null;
  aiContext?: string | null;
  dailyCostCapUsd?: number | null;
  /** Bedrock model id. Omit to leave unchanged. */
  modelId?: string;
  /** How this patient's captures reach the model. Omit to leave unchanged. */
  classifyStrategy?: ActivityClassifyStrategy;
  /** Composite shape when the strategy resolves to `merged`. Omit to leave unchanged. */
  mergeLayout?: ActivityMergeLayout;
}

export interface ActivityConfigResponse {
  id: string;
  activityRecognitionEnabled: boolean;
  activityCaptureIntervalMinutes: number | null;
  activityClipDurationSeconds: number | null;
  activityContext: string | null;
  activityDailyCostCapUsd: number | null;
  activityModelId: string;
  activityClassifyStrategy: ActivityClassifyStrategy;
  activityMergeLayout: ActivityMergeLayout;
}

// Per-patient classification settings. Mirror @okvc/schema; kept local so the
// web-app bundle doesn't pull the server-side schema lib. The model list itself
// comes from GET /activity/models (see `useActivityModels`).
export type ActivityClassifyStrategy = 'per_camera' | 'merged' | 'vision';
export type ActivityMergeLayout = 'grid' | 'sequence';

export const ACTIVITY_CLASSIFY_STRATEGIES: ReadonlyArray<{
  id: ActivityClassifyStrategy;
  label: string;
}> = [
  { id: 'per_camera', label: 'Per camera — describe each view, then decide' },
  { id: 'merged', label: 'Merged — one combined clip, one call' },
  { id: 'vision', label: 'Vision — YOLO pose and zones, no LLM' },
];

export const ACTIVITY_MERGE_LAYOUTS: ReadonlyArray<{ id: ActivityMergeLayout; label: string }> = [
  { id: 'grid', label: 'Grid — cameras side by side' },
  { id: 'sequence', label: 'Sequence — cameras back to back' },
];

export const DEFAULT_ACTIVITY_CLASSIFY_STRATEGY: ActivityClassifyStrategy = 'per_camera';
export const DEFAULT_ACTIVITY_MERGE_LAYOUT: ActivityMergeLayout = 'grid';

export interface ActivityEventReviewLog {
  id: string;
  code: ActivityEventCodeSummary;
  notes?: string | null;
}

export interface ActivityEventCodeSummary {
  id: string;
  code: string;
  description: string;
  category: { id: string; name: string; color: string } | null;
}

export interface ActivityEventCameraSummary {
  id: string;
  name: string;
}

/**
 * One person the classifier saw in one camera's view. `label` ("Person A") is
 * scoped to that view — the same label on another camera, or on the next tick,
 * is not a claim that it is the same individual.
 */
export interface ActivityPersonSighting {
  cameraId: string;
  cameraName: string;
  lighting: string;
  label: string;
  description: string;
  confidence: number;
  /** What stage 1 observed about this person; null for events written before prompt v7. */
  dimensions?: ActivityObservedDimensions | null;
}

export interface ActivityEvent {
  id: string;
  createdAt: string;
  patientId: string;
  cameraId: string;
  camera: ActivityEventCameraSummary;
  scheduledAt: string;
  capturedAt: string;
  code: ActivityEventCodeSummary;
  confidence: number;
  routingReason: string;
  attemptCount: number;
  trigger: ActivityEventTrigger;
  metadata: Record<string, unknown>;
  videoS3Key?: string | null;
  /** How many recordings fed this decision. 0 for events predating capture storage. */
  capturesCount?: number;
  people?: ActivityPersonSighting[];
  /** The patient's dimensions as the AI emitted them; null for events written before prompt v7. */
  dimensions?: ActivityDimensions | null;
  /** What the reviewer asserted (the AI's on accept); null until reviewed, or for a code-only correction. */
  reviewedDimensions?: ActivityDimensions | null;
  /** Server-computed; `high` when the model was unsure or the rules overrode it. */
  reviewPriority?: ActivityReviewPriority;
  reviewStatus?: ActivityReviewVerdict | null;
  reviewedAt?: string | null;
  reviewedById?: string | null;
  reviewLog?: ActivityEventReviewLog | null;
  deleted?: string | null;
}

/** One archived recording that fed an event's decision. */
export interface ActivityCapture {
  id: string;
  cameraId: string;
  camera: ActivityEventCameraSummary;
  /** 'normal' | 'ir' — an IR twin is a second look at the same viewpoint. */
  lighting: string;
  source: string;
  capturedAt: string;
  /** The capture the event's code was drawn from. Exactly one per event. */
  isAttributed: boolean;
  peopleCount: number | null;
  people: ActivityPersonSighting[];
  sceneSummary: string | null;
  /** Null when the clip was never archived (upload disabled or failed). */
  url: string | null;
}

export interface ActivityEventCapturesResponse {
  items: ActivityCapture[];
  /** The composite the model was shown, when the merged strategy ran. */
  mergedUrl: string | null;
  /** 'grid' | 'sequence'. Null when the per-camera strategy ran. */
  mergedLayout: string | null;
  /** The per-view label of the person the AI took as the patient; only meaningful on the attributed capture. */
  personLabel: string | null;
  expiresAt: string;
}

export interface ReviewActivityEventBody {
  verdict: ActivityReviewVerdict;
  correctCodeId?: string;
  /** Rejection only. Without `correctCodeId` the server resolves the code from these. */
  dimensions?: ActivityDimensionValues;
  notes?: string;
  metadata?: Array<{
    codeMetadataId: string;
    codeMetadataOptionId?: string;
    notes?: string;
  }>;
}

export interface ActivityEventVideoUrlResponse {
  url: string;
  expiresAt: string;
}

export interface ActivityUsageItem {
  patientId: string;
  /** The period bucket — `YYYY-MM-DD` for daily, `YYYY-MM` for monthly. */
  day: string;
  patientName?: string;
  modelId: string;
  events: number;
  inputTokens: number;
  outputTokens: number;
  avgLatencyMs: number;
  costUsd: number;
}

export interface ActivityUsageResponse {
  items: ActivityUsageItem[];
  /** The per-patient monthly budget (server config) a month's `costUsd` is compared against. */
  monthlyTargetUsd: number;
}

/** Draft overrides for the estimate; anything omitted falls back to the stored config. */
export interface GetActivityCostEstimateParams {
  intervalMinutes?: number;
  clipDurationSeconds?: number;
  modelId?: string;
  classifyStrategy?: ActivityClassifyStrategy;
  mergeLayout?: ActivityMergeLayout;
}

export type ActivityCostPerTickSource = 'empirical' | 'table';

export interface ActivityCostEstimateResponse {
  /** Ticks the scheduler would fire per day, averaged over the patient's upcoming check-in slots. */
  ticksPerDay: number;
  costPerTickUsd: number;
  /** `empirical` when averaged from the patient's own recent ticks, `table` from the static per-model figures. */
  costPerTickSource: ActivityCostPerTickSource;
  costPerDayUsd: number;
  costPerMonthUsd: number;
  targetUsd: number;
  overTarget: boolean;
}

export type ActivityUsageGranularity = 'day' | 'month';

export interface GetActivityUsageParams {
  from: string;
  to: string;
  patientId?: string;
  granularity?: ActivityUsageGranularity;
}

export type ActivityAccuracyGroupBy =
  | 'code'
  | 'patient'
  | 'camera'
  | 'model'
  | 'promptVersion'
  | 'strategy'
  | 'lighting'
  | 'dimension';

export interface GetActivityAccuracyParams {
  from: string;
  to: string;
  patientId?: string;
  cameraId?: string;
  granularity?: ActivityUsageGranularity;
  groupBy?: ActivityAccuracyGroupBy;
}

/**
 * One period × group bucket. For every `groupBy` but `dimension` a review
 * counts once, as the event's code-level verdict. For `dimension` the key is a
 * dimension name and each reviewed event carrying both the AI's and the
 * reviewer's blocks counts once per dimension, accepted when the values match.
 */
export interface ActivityAccuracyItem {
  /** Period bucket — `YYYY-MM-DD` for daily, `YYYY-MM` for monthly. */
  period: string;
  key: string;
  label: string;
  reviewed: number;
  accepted: number;
  rejected: number;
  /** accepted ÷ reviewed, null when nothing in the bucket was reviewed. */
  agreement: number | null;
}

export interface ActivityAccuracyConfusionItem {
  aiCode: string;
  reviewedCode: string;
  count: number;
}

export interface ActivityAccuracyDimensionConfusionItem {
  dimension: string;
  aiValue: string;
  reviewedValue: string;
  count: number;
}

export interface ActivityAccuracyTotals {
  events: number;
  reviewed: number;
  unreviewed: number;
  accepted: number;
  rejected: number;
  /** Code-level: accepted ÷ reviewed, whether or not the review carried dimensions. */
  agreement: number | null;
  /** Reviewed events carrying both the AI's and the reviewer's dimension blocks. */
  reviewedWithDimensions: number;
  /** Reviews that changed `presence`; `rate` is over `reviewedWithDimensions`. */
  wrongPerson: { events: number; rate: number | null };
}

export interface ActivityAccuracyWorstEntry {
  key: string;
  label: string;
  reviewed: number;
  agreement: number;
}

/** Lowest agreement over the whole range, only among keys with at least `minReviewed` reviews. */
export interface ActivityAccuracyWorst {
  minReviewed: number;
  dimension: ActivityAccuracyWorstEntry | null;
  camera: ActivityAccuracyWorstEntry | null;
}

export interface ActivityAccuracyResponse {
  totals: ActivityAccuracyTotals;
  items: ActivityAccuracyItem[];
  confusion: ActivityAccuracyConfusionItem[];
  dimensionConfusion: ActivityAccuracyDimensionConfusionItem[];
  worst: ActivityAccuracyWorst;
}

export interface ActivityEventListResponse {
  items: ActivityEvent[];
  nextCursor: string | null;
}

export interface GetActivityEventsParams {
  /** Restrict to one patient. Omit only when paired with `reviewStatus`
   * (streams-page hydration of pending events across visible patients). */
  patientId?: string;
  /** Required by the backend — `from` and `to` must straddle a real window. */
  from: string;
  to: string;
  codeId?: string;
  categoryId?: string;
  cursor?: string;
  limit?: number;
  /** `accepted` / `rejected` / `pending` (no Log yet). */
  reviewStatus?: 'accepted' | 'rejected' | 'pending';
}

export const ActivityApiKeys = {
  Events: 'ActivityEvents',
  EventVideoUrl: 'ActivityEventVideoUrl',
  EventCaptures: 'ActivityEventCaptures',
  Usage: 'ActivityUsage',
  Accuracy: 'ActivityAccuracy',
  Models: 'ActivityModels',
  CostEstimate: 'ActivityCostEstimate',
};

export class ActivityApi extends ApiClient {
  public static updateConfig(
    patientId: string,
    body: UpdateActivityConfigBody,
  ): Promise<ActivityConfigResponse> {
    return this.http
      .put<ActivityConfigResponse>(`/patients/${patientId}/activity-config`, body)
      .then((res) => res.data);
  }

  public static getCostEstimate(
    patientId: string,
    params: GetActivityCostEstimateParams,
  ): Promise<ActivityCostEstimateResponse> {
    return this.http
      .get<ActivityCostEstimateResponse>(`/patients/${patientId}/activity-cost-estimate`, { params })
      .then((res) => res.data);
  }

  public static getEvents(params: GetActivityEventsParams): Promise<ActivityEventListResponse> {
    return this.http
      .get<ActivityEventListResponse>('/activity/events', { params })
      .then((res) => res.data);
  }

  public static getEventVideoUrl(id: string): Promise<ActivityEventVideoUrlResponse> {
    return this.http
      .get<ActivityEventVideoUrlResponse>(`/activity/events/${id}/video-url`)
      .then((res) => res.data);
  }

  public static getEventCaptures(id: string): Promise<ActivityEventCapturesResponse> {
    return this.http
      .get<ActivityEventCapturesResponse>(`/activity/events/${id}/captures`)
      .then((res) => res.data);
  }

  public static getUsage(params: GetActivityUsageParams): Promise<ActivityUsageResponse> {
    return this.http
      .get<ActivityUsageResponse>('/activity/usage', { params })
      .then((res) => res.data);
  }

  public static getAccuracy(params: GetActivityAccuracyParams): Promise<ActivityAccuracyResponse> {
    return this.http
      .get<ActivityAccuracyResponse>('/activity/accuracy', { params })
      .then((res) => res.data);
  }

  public static getModels(): Promise<ActivityModelsResponse> {
    return this.http.get<ActivityModelsResponse>('/activity/models').then((res) => res.data);
  }

  public static reviewEvent(id: string, body: ReviewActivityEventBody): Promise<ActivityEvent> {
    return this.http
      .post<ActivityEvent>(`/activity/events/${id}/review`, body)
      .then((res) => res.data);
  }
}

/**
 * Toggle AR + cadence for one patient. Invalidates the cached patient row so
 * the form re-renders with the persisted values and the patient list badge
 * updates immediately.
 */
export function useUpdateActivityConfig(patientId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (body: UpdateActivityConfigBody) => ActivityApi.updateConfig(patientId, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PatientsApiKeys.Patient, patientId]);
        queryClient.invalidateQueries([PatientsApiKeys.Patients]);
      },
    },
  );
}

/**
 * What a patient's check-in cadence would cost at the given draft settings,
 * against the monthly target. Keyed on every override so the config form
 * re-estimates as fields change.
 */
export function useActivityCostEstimate(
  patientId: string,
  params: GetActivityCostEstimateParams,
  queryOptions?: UseQueryOptions<ActivityCostEstimateResponse>,
) {
  return useQuery<ActivityCostEstimateResponse>(
    [
      ActivityApiKeys.CostEstimate,
      patientId,
      params.intervalMinutes ?? null,
      params.clipDurationSeconds ?? null,
      params.modelId ?? null,
      params.classifyStrategy ?? null,
      params.mergeLayout ?? null,
    ],
    () => ActivityApi.getCostEstimate(patientId, params),
    { keepPreviousData: true, ...queryOptions },
  );
}

/**
 * Paginated activity-event timeline for one patient. Date range is mandatory;
 * the backend rejects requests without a valid `from`/`to` window. Pages are
 * keyed by the opaque server-issued `nextCursor`.
 */
export function useActivityEvents(
  params: Omit<GetActivityEventsParams, 'cursor'>,
  queryOptions?: UseInfiniteQueryOptions<ActivityEventListResponse>,
) {
  return useInfiniteQuery<ActivityEventListResponse>(
    [
      ActivityApiKeys.Events,
      params.patientId,
      params.from,
      params.to,
      params.codeId ?? null,
      params.categoryId ?? null,
      params.limit,
    ],
    ({ pageParam }) =>
      ActivityApi.getEvents({
        ...params,
        cursor: typeof pageParam === 'string' ? pageParam : undefined,
      }),
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      ...queryOptions,
    },
  );
}

/**
 * Lazy fetch a presigned playback URL for one ActivityEvent's archived clip.
 * Pass `{ enabled: true }` only when the operator actually clicks ▶ — TTLs are
 * short (10 min), so we don't want to mint URLs for every row in the widget.
 */
export function useActivityEventVideoUrl(
  id: string,
  queryOptions?: UseQueryOptions<ActivityEventVideoUrlResponse>,
) {
  return useQuery<ActivityEventVideoUrlResponse>(
    [ActivityApiKeys.EventVideoUrl, id],
    () => ActivityApi.getEventVideoUrl(id),
    queryOptions,
  );
}

/**
 * Every snippet that fed one event's decision, with presigned playback URLs.
 * Lazy like `useActivityEventVideoUrl` — the URLs expire in 10 minutes, so we
 * only mint them when the operator actually opens the gallery.
 */
export function useActivityEventCaptures(
  id: string,
  queryOptions?: UseQueryOptions<ActivityEventCapturesResponse>,
) {
  return useQuery<ActivityEventCapturesResponse>(
    [ActivityApiKeys.EventCaptures, id],
    () => ActivityApi.getEventCaptures(id),
    queryOptions,
  );
}

/**
 * Aggregated Bedrock usage grouped by patient × day × modelId. costUsd is an
 * approximate estimate based on current list pricing — not for billing.
 */
export function useActivityUsage(
  params: GetActivityUsageParams,
  queryOptions?: UseQueryOptions<ActivityUsageResponse>,
) {
  return useQuery<ActivityUsageResponse>(
    [
      ActivityApiKeys.Usage,
      params.patientId ?? 'all',
      params.from,
      params.to,
      params.granularity ?? 'day',
    ],
    () => ActivityApi.getUsage(params),
    queryOptions,
  );
}

/**
 * The models an operator may assign to a patient, with capability and the
 * fleet default. Served by the backend registry so the dropdown never drifts
 * from what the classifier can actually run.
 */
export function useActivityModels(queryOptions?: UseQueryOptions<ActivityModelsResponse>) {
  return useQuery<ActivityModelsResponse>(
    [ActivityApiKeys.Models],
    () => ActivityApi.getModels(),
    { staleTime: Infinity, ...queryOptions },
  );
}

/**
 * Agreement between the AI code and the monitor's review, bucketed by period
 * and one grouping dimension, plus the confusion of rejected events.
 */
export function useActivityAccuracy(
  params: GetActivityAccuracyParams,
  queryOptions?: UseQueryOptions<ActivityAccuracyResponse>,
) {
  return useQuery<ActivityAccuracyResponse>(
    [
      ActivityApiKeys.Accuracy,
      params.patientId ?? 'all',
      params.cameraId ?? 'all',
      params.from,
      params.to,
      params.granularity ?? 'month',
      params.groupBy ?? 'code',
    ],
    () => ActivityApi.getAccuracy(params),
    queryOptions,
  );
}

/**
 * Accept / reject an AI activity event. Server creates a Log row (with the AI's
 * code on accept, the operator's correction on reject) and flips
 * `event.reviewStatus`. The caller is expected to optimistically dispatch
 * `activityEventRemoved(id)` so the toast/drawer reacts immediately.
 */
export function useReviewActivityEvent(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (body: ReviewActivityEventBody) => ActivityApi.reviewEvent(eventId, body),
    {
      onSuccess: (event) => {
        queryClient.invalidateQueries([ActivityApiKeys.Events, event.patientId]);
        // The review path creates a Log row server-side. Refresh the latest-log
        // cache for the patient so every `useLiveLatestLog` observer mounted in
        // StreamControls / PatientStreamContainer / PatientProfileBarContent
        // re-fetches. We can't rely on the WS LOG_UPDATED frame here because the
        // backend also broadcasts ACTIVITY_EVENT_REVIEWED in the same tick and
        // React 18 batches them — the LOG_UPDATED frame loses the race.
        queryClient.invalidateQueries([LogsApiKeys.GET_LATEST_LOG, event.patientId]);
      },
    },
  );
}

/**
 * Non-paginated single-page read used to hydrate the pending-events Redux slice
 * on streams-page mount. Caller passes a 30-min window ending now; the slice
 * filters out anything already reviewed.
 */
export function usePendingActivityEvents(
  params: Omit<GetActivityEventsParams, 'cursor' | 'limit'>,
  queryOptions?: UseQueryOptions<ActivityEventListResponse>,
) {
  return useQuery<ActivityEventListResponse>(
    [ActivityApiKeys.Events, 'pending', params.patientId, params.from, params.to],
    () => ActivityApi.getEvents({ ...params, limit: 100 }),
    queryOptions,
  );
}
